// Base color system
export const rawColor = {
  blue: {
    main: "#2196F3",
    dark: "#1565C0",
    faint: "rgba(33, 150, 243, 0.08)",
    "faint-darker": "rgba(33, 150, 243, 0.3)",
    "faint-lighter": "rgba(33, 150, 243, 0.04)",
  },
  oceanBlue: {
    main: "#0EA5E9",
    secondary: "#38BDF8",
    background: "#0C1A2A",
    paper: "#1E293B",
    text: {
      primary: "#E2E8F0", // Light gray-blue for text
      secondary: "#94A3B8", // Slightly darker gray-blue for secondary text
    },
    sidebar: {
      base: "#1E293B",
      hover: "rgba(14, 165, 233, 0.08)",
      selected: "rgba(14, 165, 233, 0.12)",
    },
    table: {
      cell: "#1E293B",
      header: "#0C1A2A",
    },
    divider: "rgba(226, 232, 240, 0.12)", // Light gray-blue with low opacity
  },
  lightBlue: {
    main: "#7DD3FC", // Soft baby blue
    secondary: "#38BDF8", // Slightly deeper baby blue
    background: "#F8FAFC", // Off-white background
    paper: "#FFFFFF",
    text: {
      primary: "#0C4A6E", // Deep blue for text
      secondary: "#0369A1", // Slightly lighter blue for secondary text
    },
    sidebar: {
      base: "#F0F9FF",
      hover: "rgba(125, 211, 252, 0.08)",
      selected: "rgba(125, 211, 252, 0.12)",
    },
    table: {
      cell: "#F8FAFC", // Very subtle blue-white for table cells
      header: "#F1F5F9", // Slightly more blue tint for headers
    },
    divider: "rgba(12, 74, 110, 0.12)", // Deep blue with low opacity
  },
  gold: {
    main: "#B89B4C",
    light: "#FFD700",
    faint: "rgba(184, 155, 76, 0.18)",
  },
  forestGreen: {
    main: "#2E7D32",
    light: "#388E3C",
    dark: "#1a1f1c",
    paper: "#232825",
    hover: "rgba(46, 125, 50, 0.08)",
    selected: "rgba(46, 125, 50, 0.12)",
    text: {
      primary: "#E5E5E5",
      secondary: "#B0B0B0",
    },
    sidebar: {
      base: "#232825",
      hover: "rgba(46, 125, 50, 0.08)",
      selected: "rgba(46, 125, 50, 0.12)",
    },
    table: {
      cell: "#232825",
      header: "#1a1f1c",
    },
    divider: "rgba(255, 255, 255, 0.12)",
  },
  text: {
    primary: {
      light: "#232323",
      dark: "#CCCCCC",
    },
    secondary: {
      light: "#666666",
      dark: "#B0B0B0",
    },
  },
  background: {
    light: {
      default: "#F7F9FB",
      paper: "rgb(236, 242, 247, 0.1)",
    },
    dark: {
      default: "#181818",
      paper: "#232323",
    },
  },
  status: {
    error: {
      light: "#E53935",
      dark: "#E57373",
      red: "#ef4444",
    },
    warning: "#FFB300",
    active: {
      main: "#43A047",
      green: "#22c55e",
    },
  },
  divider: {
    light: "rgba(0, 0, 0, 0.12)",
    dark: "rgba(255, 255, 255, 0.12)",
  },
  white: {
    main: "#FFFFFF",
    transparent: {
      "12": "rgba(255, 255, 255, 0.12)",
      "08": "rgba(255, 255, 255, 0.08)",
      "07": "rgba(255, 255, 255, 0.7)",
      "05": "rgba(255, 255, 255, 0.05)",
      "04": "rgba(255, 255, 255, 0.04)",
      "03": "rgba(255, 255, 255, 0.3)",
      "02": "rgba(255, 255, 255, 0.05)",
      "01": "rgba(255, 255, 255, 0.01)",
    },
  },
  black: {
    main: "#000000",
    transparent: {
      "54": "rgba(0, 0, 0, 0.54)",
      "12": "rgba(0, 0, 0, 0.12)",
      "08": "rgba(0, 0, 0, 0.08)",
      "06": "rgba(0, 0, 0, 0.06)",
      "05": "rgba(0, 0, 0, 0.05)",
      "04": "rgba(0, 0, 0, 0.04)",
      "03": "rgba(0, 0, 0, 0.03)",
      "02": "rgba(0, 0, 0, 0.05)",
      "01": "rgba(0, 0, 0, 0.01)",
    },
  },
  table: {
    header: {
      light: "#78909C",
      dark: "#1E1E1E",
    },
    cell: {
      light: "rgba(30, 150, 215, 0.015)",
    },
  },
  shadow: {
    light: {
      small: "0 2px 8px rgba(0, 0, 0, 0.1)",
      medium: "0 4px 20px rgba(0, 0, 0, 0.08)",
      large: "0 4px 12px rgba(0, 0, 0, 0.3)",
    },
    dark: {
      small: "0 2px 8px rgba(0, 0, 0, 0.2)",
      medium: "0 4px 20px rgba(0, 0, 0, 0.15)",
      large: "0 4px 12px rgba(0, 0, 0, 0.4)",
    },
  },
  avatar: {
    colors: [
      "#C1121F", // Deep Red
      "#1A5F7A", // Deep Teal
      "#B08968", // Deep Brown
      "#1B3B6F", // Deep Navy
      "#BC6C25", // Deep Orange
      "#5A189A", // Deep Purple
      "#0B6E4F", // Deep Green
      "#9B1D20", // Deep Burgundy
      "#0D47A1", // Deep Blue
      "#B45309", // Deep Amber
      "#1E3A8A", // Deep Indigo
      "#7E22CE", // Deep Violet
    ],
    patterns: [
      "linear-gradient(45deg, transparent 25%, rgba(255,255,255,0.3) 25%, rgba(255,255,255,0.3) 50%, transparent 50%, transparent 75%, rgba(255,255,255,0.3) 75%, rgba(255,255,255,0.3))",
      "linear-gradient(135deg, transparent 25%, rgba(255,255,255,0.3) 25%, rgba(255,255,255,0.3) 50%, transparent 50%, transparent 75%, rgba(255,255,255,0.3) 75%, rgba(255,255,255,0.3))",
      "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.3) 0%, transparent 50%)",
      "linear-gradient(to right, rgba(255,255,255,0.3) 0%, transparent 50%, rgba(255,255,255,0.3) 100%)",
    ],
  },
  pastel: {
    main: "#D2C2B5", // Slightly more mocha warm gray
    secondary: "#E6DCD0", // Slightly more mocha cream
    background: "#FCF7F2", // Slightly more mocha off-white
    paper: "#FCF7F2",
    text: {
      primary: "#695A4D", // Slightly more mocha brown
      secondary: "#988A7C", // Slightly more mocha light brown
    },
    sidebar: {
      base: "#FCF7F2",
      hover: "rgba(210, 194, 181, 0.08)",
      selected: "rgba(210, 194, 181, 0.12)",
    },
    table: {
      cell: "#FCF7F2",
      header: "#F3EDE6",
    },
    divider: "rgba(105, 90, 77, 0.12)", // Slightly more mocha brown with low opacity
  },
  stone: {
    main: "#78716C", // Warm stone gray
    secondary: "#A8A29E", // Lighter stone gray
    background: "#F5F5F4", // Warm off-white
    paper: "#FFFFFF",
    text: {
      primary: "#44403C", // Dark stone
      secondary: "#78716C", // Medium stone
    },
    sidebar: {
      base: "#F5F5F4",
      hover: "rgba(120, 113, 108, 0.08)",
      selected: "rgba(120, 113, 108, 0.12)",
    },
    table: {
      cell: "#FFFFFF",
      header: "#F5F5F4",
    },
    divider: "rgba(68, 64, 60, 0.12)", // Dark stone with low opacity
  },
  exyboxGold: {
    main: "#B89B4C", // Rich gold
    secondary: "#D4AF37", // Bright gold
    background: "#1A1A1A", // Dark background
    paper: "#232323", // Slightly lighter dark
    text: {
      primary: "#E5E5E5", // Light gray
      secondary: "#B89B4C", // Gold for secondary text
    },
    sidebar: {
      base: "#232323",
      hover: "rgba(184, 155, 76, 0.08)",
      selected: "rgba(184, 155, 76, 0.12)",
    },
    table: {
      cell: "#232323",
      header: "#1A1A1A",
    },
    divider: "rgba(184, 155, 76, 0.12)", // Gold with low opacity
  },
  volcano: {
    main: "#B89B4C", // Rich gold
    secondary: "#D4AF37", // Bright gold
    background: "#1A1A1A", // Dark background
    paper: "#232323", // Slightly lighter dark
    text: {
      primary: "#E5E5E5", // Light gray
      secondary: "#A8A29E", // Muted gray instead of gold
    },
    sidebar: {
      base: "#232323",
      hover: "rgba(184, 155, 76, 0.08)",
      selected: "rgba(184, 155, 76, 0.12)",
    },
    table: {
      cell: "#232323",
      header: "#1A1A1A",
    },
    divider: "rgba(255, 255, 255, 0.12)", // White with low opacity instead of gold
  },
  sage: {
    main: "#9CAF88", // Soft sage green
    secondary: "#B5C4A9", // Lighter sage green
    background: "#F5F7F3", // Very light sage background
    paper: "#FFFFFF",
    text: {
      primary: "#4A5D3F", // Dark sage green
      secondary: "#6B7B5F", // Medium sage green
    },
    sidebar: {
      base: "#F5F7F3",
      hover: "rgba(156, 175, 136, 0.08)",
      selected: "rgba(156, 175, 136, 0.12)",
    },
    table: {
      cell: "#FFFFFF",
      header: "#F5F7F3",
    },
    divider: "rgba(74, 93, 63, 0.12)", // Dark sage with low opacity
  },
  espresso: {
    main: "#D4B483", // Rich gold-brown
    secondary: "#B89B4C", // Warm amber-brown
    background: "#1A1814", // Dark chocolate background
    paper: "#2A2520", // Slightly lighter dark chocolate
    text: {
      primary: "#E5E0D9", // Light cream
      secondary: "#D4B483", // Rich gold-brown
    },
    sidebar: {
      base: "#2A2520",
      hover: "rgba(212, 180, 131, 0.08)",
      selected: "rgba(212, 180, 131, 0.12)",
    },
    table: {
      cell: "#2A2520",
      header: "#1A1814",
    },
    divider: "rgba(212, 180, 131, 0.12)", // Gold-brown with low opacity
  },
} as const;

// UI-specific colors
export const colors = {
  // Component states
  componentHighlight: rawColor.blue.faint,
  rippleEffect: rawColor.blue["faint-darker"],

  // Sidebar colors
  sidebar: {
    light: {
      base: rawColor.background.light.default,
      hover: rawColor.blue["faint-lighter"],
      selected: rawColor.blue.faint,
    },
    dark: {
      base: rawColor.background.dark.paper,
      hover: rawColor.white.transparent["12"],
      selected: rawColor.white.transparent["08"],
    },
    forestGreen: {
      base: rawColor.forestGreen.paper,
      hover: rawColor.forestGreen.hover,
      selected: rawColor.forestGreen.selected,
    },
    lightBlue: {
      base: rawColor.lightBlue.sidebar.base,
      hover: rawColor.lightBlue.sidebar.hover,
      selected: rawColor.lightBlue.sidebar.selected,
    },
    oceanBlue: {
      base: rawColor.oceanBlue.sidebar.base,
      hover: rawColor.oceanBlue.sidebar.hover,
      selected: rawColor.oceanBlue.sidebar.selected,
    },
    pastel: {
      base: rawColor.pastel.sidebar.base,
      hover: rawColor.pastel.sidebar.hover,
      selected: rawColor.pastel.sidebar.selected,
    },
    stone: {
      base: rawColor.stone.sidebar.base,
      hover: rawColor.stone.sidebar.hover,
      selected: rawColor.stone.sidebar.selected,
    },
    exyboxGold: {
      base: rawColor.exyboxGold.sidebar.base,
      hover: rawColor.exyboxGold.sidebar.hover,
      selected: rawColor.exyboxGold.sidebar.selected,
    },
    volcano: {
      base: rawColor.volcano.sidebar.base,
      hover: rawColor.volcano.sidebar.hover,
      selected: rawColor.volcano.sidebar.selected,
    },
    sage: {
      base: rawColor.sage.sidebar.base,
      hover: rawColor.sage.sidebar.hover,
      selected: rawColor.sage.sidebar.selected,
    },
    espresso: {
      base: rawColor.espresso.sidebar.base,
      hover: rawColor.espresso.sidebar.hover,
      selected: rawColor.espresso.sidebar.selected,
    },
  },

  // Table colors
  table: {
    light: {
      cell: rawColor.table.cell.light,
      header: rawColor.blue.faint,
      headerText: rawColor.table.header.light,
    },
    dark: {
      cell: rawColor.background.dark.paper,
      header: rawColor.table.header.dark,
      headerText: rawColor.gold.main,
    },
    forestGreen: {
      cell: rawColor.forestGreen.paper,
      header: rawColor.forestGreen.dark,
      headerText: rawColor.forestGreen.main,
    },
    lightBlue: {
      cell: rawColor.lightBlue.table.cell,
      header: rawColor.lightBlue.table.header,
      headerText: rawColor.lightBlue.text.secondary,
    },
    oceanBlue: {
      cell: rawColor.oceanBlue.table.cell,
      header: rawColor.oceanBlue.table.header,
      headerText: rawColor.oceanBlue.main,
    },
    pastel: {
      cell: rawColor.pastel.table.cell,
      header: rawColor.pastel.table.header,
      headerText: rawColor.pastel.text.secondary,
    },
    stone: {
      cell: rawColor.stone.table.cell,
      header: rawColor.stone.table.header,
      headerText: rawColor.stone.text.secondary,
    },
    exyboxGold: {
      cell: rawColor.exyboxGold.table.cell,
      header: rawColor.exyboxGold.table.header,
      headerText: rawColor.exyboxGold.main,
    },
    volcano: {
      cell: rawColor.volcano.table.cell,
      header: rawColor.volcano.table.header,
      headerText: rawColor.volcano.text.primary,
    },
    sage: {
      cell: rawColor.sage.table.cell,
      header: rawColor.sage.table.header,
      headerText: rawColor.sage.text.secondary,
    },
    espresso: {
      cell: rawColor.espresso.table.cell,
      header: rawColor.espresso.table.header,
      headerText: rawColor.espresso.text.secondary,
    },
  },

  // Icon colors
  icon: {
    light: rawColor.black.transparent["54"],
    dark: rawColor.white.transparent["07"],
  },

  // Shadow colors
  shadow: {
    light: rawColor.shadow.light,
    dark: rawColor.shadow.dark,
  },

  // Overlay colors
  overlay: {
    light: {
      default: rawColor.black.transparent["08"],
      hover: rawColor.black.transparent["12"],
    },
    dark: {
      default: rawColor.white.transparent["04"],
      hover: rawColor.white.transparent["07"],
    },
  },
} as const;
